import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, RotateCcw, Search } from "lucide-react";
import { API } from "@/lib/site";

const MODE_BADGE = {
  OBD: "bg-regal-amber/10 text-regal-amber",
  BENCH: "bg-sky-50 text-sky-700",
  BOOT: "bg-emerald-50 text-emerald-700",
};

const inputCls = "h-11 w-full border border-line bg-white px-3 text-sm text-ink outline-none focus:border-regal-amber transition-colors";
const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500";

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className={labelCls} htmlFor={testId}>{label}</label>
      <select id={testId} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

/**
 * Native vehicle compatibility checker. Data is served from our own API
 * (MongoDB snapshot imported from the authorised tool manufacturer's data).
 * Props let each brand page brand it as its own (e.g. KESS3).
 */
export function VehicleCompatibility({ apiBase, testId, ctaTo, ctaLabel, heading, intro }) {
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState(null);
  const [meta, setMeta] = useState({ total: 0, source: "" });
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const PER_PAGE = 15;

  const params = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    if (search.trim()) p.set("search", search.trim());
    return p;
  }, [filters, search]);

  useEffect(() => {
    axios.get(`${API}/${apiBase}/filters?${params}`)
      .then((r) => { setOptions(r.data.options); setMeta({ total: r.data.total, source: r.data.source || "" }); })
      .catch(() => {});
  }, [apiBase, params]);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    const p = new URLSearchParams(params);
    p.set("page", page);
    p.set("per_page", PER_PAGE);
    axios.get(`${API}/${apiBase}?${p}`)
      .then((r) => { setRows(r.data.rows); setCount(r.data.count); })
      .catch(() => { setRows([]); setCount(0); setError(true); })
      .finally(() => setLoading(false));
  }, [apiBase, params, page]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const reset = () => { setFilters({}); setSearch(""); setPage(1); };
  const pages = Math.max(Math.ceil(count / PER_PAGE), 1);
  const activeCount = Object.values(filters).filter(Boolean).length + (search.trim() ? 1 : 0);
  const isSample = meta.source.includes("sample");

  return (
    <div data-testid={testId}>
      <p className="overline-label mb-4">Vehicle Compatibility</p>
      <h2 className="max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">{heading}</h2>
      <p className="mt-4 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">{intro}</p>
      {isSample && (
        <p className="mt-4 border border-line bg-white p-4 text-xs text-neutral-500" data-testid={`${testId}-sample-note`}>
          Showing a sample of the database while the full authorised vehicle data is being connected. Can't see your vehicle? <Link to="/contact" className="text-regal-amber underline-offset-4 hover:underline">Ask us to check coverage</Link> — we confirm compatibility before you buy.
        </p>
      )}

      <div className="mt-8 border border-line bg-white p-6 sm:p-8" data-testid={`${testId}-filters`}>
        <div className="relative mb-6">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Search make, model, engine code or ECU…"
            className={`${inputCls} pl-9`}
            data-testid={`${testId}-search`}
          />
        </div>
        {options && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FilterSelect label="Vehicle Type" value={filters.vehicle_type || ""} onChange={(v) => setFilter("vehicle_type", v)} options={options.vehicle_type} testId={`${testId}-filter-type`} />
            <FilterSelect label="Make / Brand" value={filters.brand || ""} onChange={(v) => { setFilters((f) => ({ ...f, brand: v, model: "" })); setPage(1); }} options={options.brand} testId={`${testId}-filter-brand`} />
            <FilterSelect label="Model" value={filters.model || ""} onChange={(v) => setFilter("model", v)} options={options.model} testId={`${testId}-filter-model`} />
            <FilterSelect label="Fuel" value={filters.fuel || ""} onChange={(v) => setFilter("fuel", v)} options={options.fuel} testId={`${testId}-filter-fuel`} />
            <FilterSelect label="Model Year" value={filters.year || ""} onChange={(v) => setFilter("year", v)} options={options.year} testId={`${testId}-filter-year`} />
            <FilterSelect label="ECU Manufacturer" value={filters.ecu_maker || ""} onChange={(v) => setFilter("ecu_maker", v)} options={options.ecu_maker} testId={`${testId}-filter-ecu-maker`} />
            {options.ecu_type && (
              <div>
                <label className={labelCls} htmlFor={`${testId}-filter-ecu-type`}>Control Unit</label>
                <select id={`${testId}-filter-ecu-type`} data-testid={`${testId}-filter-ecu-type`} value={filters.ecu_type || ""} onChange={(e) => setFilter("ecu_type", e.target.value)} className={inputCls}>
                  <option value="">All</option>
                  {options.ecu_type.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            )}
            <FilterSelect label="Connection Mode" value={filters.mode || ""} onChange={(v) => setFilter("mode", v)} options={options.modes} testId={`${testId}-filter-mode`} />
          </div>
        )}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" data-testid={`${testId}-count`}>
            {loading ? "Searching…" : `${count.toLocaleString()} matching application${count === 1 ? "" : "s"}`}
          </p>
          <div className="flex items-center gap-3">
            {activeCount > 0 && (
              <button onClick={reset} data-testid={`${testId}-reset`} className="inline-flex items-center gap-2 border border-linebright px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
                <RotateCcw size={13} /> Reset
              </button>
            )}
            <a href={ctaTo} data-testid={`${testId}-cta`} className="inline-flex items-center gap-2 bg-regal-amber px-6 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
              {ctaLabel} <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto border border-line" data-testid={`${testId}-results`}>
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-white">
              {["Vehicle", "Engine", "Fuel", "Power", "Year", "ECU / TCU", "Connection"].map((h) => (
                <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {error && (
              <tr><td colSpan={7} className="px-4 py-14 text-center text-sm text-neutral-400" data-testid={`${testId}-error`}>The compatibility database couldn't be loaded just now — please try again, or <Link to="/contact" className="text-regal-amber underline-offset-4 hover:underline">contact us</Link> and we'll check coverage for you.</td></tr>
            )}
            {!error && !loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-14 text-center text-sm text-neutral-400" data-testid={`${testId}-empty`}>No applications match those filters. Try widening your search — or <Link to="/contact" className="text-regal-amber underline-offset-4 hover:underline">ask us to check coverage</Link>.</td></tr>
            )}
            {rows.map((v, idx) => (
              <tr key={`${v.mm_id}-${idx}`} className="border-b border-line bg-white last:border-0 hover:bg-surface/60 transition-colors" data-testid={`${testId}-row`}>
                <td className="px-4 py-3.5">
                  <p className="font-display text-sm font-bold uppercase tracking-wide text-ink">{v.brand} {v.model}</p>
                  <p className="text-xs text-neutral-500">{v.vehicle_type}{v.generation ? ` · ${v.generation}` : ""}{v.version ? ` · ${v.version}` : ""}</p>
                </td>
                <td className="px-4 py-3.5">
                  <p className="font-mono text-xs text-ink">{v.engine || v.engine_code || "—"}</p>
                  {v.engine_manufacturer && <p className="text-[11px] text-neutral-500">{v.engine_manufacturer}</p>}
                </td>
                <td className="px-4 py-3.5 text-neutral-400">{v.fuel}</td>
                <td className="px-4 py-3.5 text-neutral-400">{v.power_display || (v.kw ? `${v.kw} kW` : "—")}</td>
                <td className="px-4 py-3.5 text-neutral-400">{v.year || "—"}</td>
                <td className="px-4 py-3.5">
                  {v.ecus?.slice(0, 2).map((e, i) => (
                    <p key={i} className="text-xs text-ink">
                      {e.name}
                      {e.micro ? <span className="text-neutral-500"> · {e.micro}</span> : null}
                      {e.type ? <span className="ml-1 font-mono text-[10px] uppercase tracking-[0.1em] text-regal-amber">{e.type === "gearbox" ? "TCU" : e.type === "engine" ? "ECU" : e.type}</span> : null}
                    </p>
                  ))}
                  {v.ecus?.length > 2 && <p className="font-mono text-[10px] text-neutral-400">+{v.ecus.length - 2} more</p>}
                </td>
                <td className="px-4 py-3.5">
                  <div className="flex flex-wrap gap-1.5">
                    {(v.modes || []).map((m) => (
                      <span key={m} className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${MODE_BADGE[m] || "bg-surface text-neutral-500"}`}>{m}</span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="mt-6 flex items-center justify-between" data-testid={`${testId}-pagination`}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid={`${testId}-prev`} className="border border-linebright px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber disabled:opacity-30 transition-colors">Previous</button>
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">Page {page} of {pages.toLocaleString()}</p>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid={`${testId}-next`} className="border border-linebright px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber disabled:opacity-30 transition-colors">Next</button>
        </div>
      )}
    </div>
  );
}
